import { useRemount } from '@workday/wcp-cargo-react';
import { useContext, useMemo } from 'react';
import { DevsiteCookies } from 'shared/models/cookies';
import { setCookie } from 'modules/common/DevsiteCookies';
import { PopupContext } from 'modules/common/components/PopupContext';
import { ToastContext } from 'modules/common/components/Toast';
import useBlockingProcess from 'modules/common/hooks/useBlockingProcess';
import useBroadcaster from 'modules/common/hooks/useBroadcaster';
import useGetInBackground from 'modules/common/hooks/useGetInBackground';
import useGetOnMount from 'modules/common/hooks/useGetOnMount';
import { HttpClientResponse } from 'modules/common/requests/Definitions';
import { OctoPaasEndpointV2, OctopaasExecutor } from 'modules/common/requests/ExecutorFactory';
import { getAllPaginatedData } from 'modules/common/requests/utils';
import { Analytics } from 'modules/common/util/Analytics';
import { DEVSITE_LOCALSTORAGE, removeLocalStorage } from 'modules/common/util/DevsiteLocalStorage';
import wconsole from 'modules/common/util/wconsole';
import { useSandboxPermissions } from '../hooks/useSandboxPermissions';
import { CompanyAssociationTypes, CompanyUser, UserCompany, UserCompanyBase } from '../models/orgs';
import { PackagesOrgRole } from '../models/package';
import { fetchOrg, isPrimary } from '../utils/orgs';
import { OrgContext, OrgContextModel, defaultOrgContext } from './OrgProvider';
import { SwitchedCompanyPopup } from './SwitchedCompanyPopup';

interface OrgProviderContentProps {
  currentOrg: UserCompany;
  setCurrentOrg: (org: UserCompany) => void;
  orgs: UserCompanyBase[];
  refreshOrgs: () => void;
  hideAppWrapper?: boolean;
}

export const OrgProviderContent: React.FC<React.PropsWithChildren<OrgProviderContentProps>> = (props) => {
  const { orgs, refreshOrgs, currentOrg, setCurrentOrg } = props;
  const popupContext = useContext(PopupContext);
  const toastContext = useContext(ToastContext);
  const process = useBlockingProcess();

  const { renderRemountable, remount } = useRemount();
  const currentOrgBroadcaster = useBroadcaster<UserCompany>('current-org-broadcast', (org: UserCompany) => {
    props.setCurrentOrg(org);
    toastContext.clearToasts();

    remount();

    popupContext.display(<SwitchedCompanyPopup org={org} popupContext={popupContext} />, {
      heading: 'Company Switched',
    });
  });

  const currentOrgMembers = useGetInBackground<CompanyUser[]>(
    async () =>
      getAllPaginatedData<CompanyUser>(
        OctopaasExecutor,
        OctoPaasEndpointV2.ORG_MEMBERS_WITH_PARAMS(currentOrg.organizationUuid),
        100,
        (response: HttpClientResponse) => ({
          data: response.body.data,
          total: response.body.total,
        })
      ),
    [currentOrg]
  );

  const orgPermissions = useGetOnMount<{ permissions: string[] }>(
    OctopaasExecutor,
    OctoPaasEndpointV2.ORG_PERMISSIONS(currentOrg.organizationUuid, 'self'),
    (res) => res.body,
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    null,
    { deleteDataOnDepChange: true }
  );

  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  const baseContext = useMemo<OrgContextModel>(() => {
    if (!currentOrg) {
      return { ...defaultOrgContext, refreshOrgs };
    }
    const isPrimaryOrg = isPrimary(currentOrg);
    const roles = currentOrg.roles;
    const isCollaborator = currentOrg.associationType === CompanyAssociationTypes.COLLABORATOR;
    const isOrgAdmin = roles.includes(PackagesOrgRole.ADMIN);
    const isDeveloper = roles.includes(PackagesOrgRole.DEVELOPER);
    const isDsc = roles.includes(PackagesOrgRole.DSC);
    const isSpecialist = roles.includes(PackagesOrgRole.SPECIALIST);
    const isMember = roles.includes(PackagesOrgRole.MEMBER);
    const hasNoOrgs = !props.orgs || props.orgs.length === 0;
    const isAWS = currentOrg.isAWS;
    const getMember = async (uuid: string) => {
      const members = await currentOrgMembers;
      return members?.find((mem) => mem.userUuid === uuid);
    };

    Analytics.setGroup('region', currentOrg.region);
    Analytics.setGroup('organizationType', currentOrg.organizationType);

    const getCurrentOrgUuid = () => currentOrg.organizationUuid;
    const hasPermission: OrgContextModel['hasPermission'] = (permission) => {
      if (!orgPermissions.data) return false;
      if (typeof permission === 'string') return orgPermissions.data?.permissions.includes(permission);
      return permission.every((p) => orgPermissions.data?.permissions.includes(p));
    };

    const hasSomePermission: OrgContextModel['hasSomePermission'] = (permission) => {
      if (!orgPermissions.data) return false;
      if (typeof permission === 'string') return orgPermissions.data?.permissions.includes(permission);
      return permission.some((p) => orgPermissions.data?.permissions.includes(p));
    };

    const setCurrentOrgAndNotify = async (org: UserCompanyBase, backgroundSwitch: boolean = false) => {
      const switchOrg = () => fetchOrg(org, { trigger: 'Switch', backgroundSwitch });
      try {
        const userCompany = backgroundSwitch
          ? await switchOrg()
          : await process(switchOrg, { title: 'Switching Company', delayUXBlocking: 500 });
        setCurrentOrg(userCompany);
        setCookie(DevsiteCookies.WCP_ORG_UUID, userCompany.organizationUuid);
        if (!backgroundSwitch) {
          toastContext.clearToasts();
          currentOrgBroadcaster.dispatch(userCompany);
          removeLocalStorage(DEVSITE_LOCALSTORAGE.TENANT_EXPIRY_TOAST_DISMISSED);
          remount();
          toastContext.success(`Switched to company: ${org.organizationName}`);
        }
      } catch (error) {
        // Background switches reject so the caller can show its own error
        if (backgroundSwitch) throw error;
        wconsole.error('Failed to switch company:', error);
        toastContext.error(`Unable to switch to ${org.organizationName}. Try again later.`);
      }
    };

    return {
      currentOrg,
      setCurrentOrg: setCurrentOrgAndNotify,
      hasNoOrgs,
      refreshOrgs,
      isPrimaryOrg,
      isOrgAdmin,
      isDeveloper,
      isCollaborator,
      isDsc,
      isSpecialist,
      isMember,
      isAWS,
      getCurrentOrgUuid,
      hasPermission,
      hasSomePermission,
      refreshOrgPermissions: orgPermissions.refresh,
      getMember,
      orgs,
    };
  }, [currentOrg, currentOrgMembers, orgPermissions.data, orgPermissions.refresh, toastContext]);

  // If sandbox is enabled, useSandboxPermissions will start a message listener and override hasPermission and hasSomePermission with sandboxed permissions
  const contextValue = useSandboxPermissions(baseContext);

  return (
    <OrgContext.Provider value={contextValue}>
      {!orgPermissions.loading && renderRemountable(props.children)}
    </OrgContext.Provider>
  );
};
